// Los reordenamientos de la ficha de un producto —
// ADR-2026-09-07-lanzamientos-fotos-por-variante.
//
// El `orden` de una foto es del PRODUCTO, no de la variante: es el que define la
// portada y el orden de la galería en la tienda. Cuando las fotos se muestran
// separadas por variante, el usuario arrastra dentro de UNA tira, pero lo que hay
// que guardar sigue siendo un orden global.
//
// La regla es que arrastrar dentro de una tira no puede mover las fotos de las
// otras: la tira se reordena en los MISMOS lugares que ya ocupaba en la lista
// global. Si las fotos de una variante estaban primera y tercera, después de
// arrastrarlas siguen siendo la primera y la tercera, con otro contenido.

/** Lo mínimo que necesita saber de una foto para ordenarla. */
export interface ConId {
  id: string;
}

/**
 * Devuelve el orden global nuevo después de reordenar UNA tira.
 *
 * `todas` va en el orden global actual. `idsDelGrupoEnOrden` es cómo quedó la
 * tira después del arrastre. Los ids del grupo que no estén en `todas` se
 * ignoran, y los lugares que sobran se rellenan con lo que ya había: nunca
 * devuelve una lista más corta que la original ni pierde una foto.
 */
export function reordenarDentroDelGrupo(
  todas: readonly ConId[],
  idsDelGrupoEnOrden: readonly string[],
): string[] {
  const globales = todas.map((f) => f.id);
  const delGrupo = idsDelGrupoEnOrden.filter((id) => globales.includes(id));
  const lugares = globales
    .map((id, i) => (delGrupo.includes(id) ? i : -1))
    .filter((i) => i !== -1);

  const resultado = [...globales];
  lugares.forEach((lugar, i) => {
    const id = delGrupo[i];
    if (id !== undefined) resultado[lugar] = id;
  });
  return resultado;
}

/**
 * Las fotos de una variante, en el orden global.
 *
 * `null` devuelve las que no son de ninguna: con las fotos por variante
 * prendidas esas no se ven en ninguna tira, y una foto invisible igual se
 * publica en la galería. Por eso hay que poder mostrarlas aparte.
 */
export function fotosDeVariante<T extends { lanzamientoVarianteId: string | null }>(
  todas: readonly T[],
  varianteId: string | null,
): T[] {
  return todas.filter((f) => f.lanzamientoVarianteId === varianteId);
}

/**
 * Mueve un id un escalón arriba (`-1`) o abajo (`+1`), intercambiándolo con su
 * vecino. Lo usan las flechas de las variantes.
 *
 * En los extremos devuelve la lista igual en vez de dar la vuelta: los botones
 * ya están apagados ahí, y que "subir" desde el primer lugar lo mandara al final
 * sería lo contrario de lo que dice la flecha.
 */
export function moverUnEscalon(
  ids: readonly string[],
  id: string,
  hacia: -1 | 1,
): string[] {
  const desde = ids.indexOf(id);
  const hasta = desde + hacia;
  if (desde === -1 || hasta < 0 || hasta >= ids.length) return [...ids];
  const siguiente = [...ids];
  siguiente[desde] = ids[hasta]!;
  siguiente[hasta] = ids[desde]!;
  return siguiente;
}
